import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, sms } from '@cwos/api-client';
import { ConsequenceNote } from '@cwos/ui-admin';
import { ListState } from '../ListStates.js';
import { listPhase } from '../listState.js';
import { SettingsCard, SettingsField, SettingsHeader } from './common.js';

/**
 * Notifications & SMS (doc 01 A6, doc 13 T3; ADR 0013).
 *
 * THIS SCREEN HAS ASKED FOR THE WRONG THING TWICE, AND THE SECOND TIME IS THE
 * INTERESTING ONE.
 *
 * The first version showed two `SecretField`s labelled "Twilio Account SID" and
 * "Twilio Auth Token" and invited an admin to paste live credentials, which the
 * API did not accept and Zod silently stripped. The second replaced them with
 * two plain-text "reference" fields carrying Secret Manager resource paths. That
 * closed the leak — a path is not a secret — but it still described a
 * PER-ORGANIZATION credential model this product does not have, and the prose
 * under it had to spend a paragraph disowning the fields above it.
 *
 * WHAT IS TRUE. SMS is a DEPLOYMENT-level integration: one Twilio account serves
 * every organization on a deployment, `twilio-connector.ts` reads
 * `TWILIO_ACCOUNT_SID` / `TWILIO_AUTH_TOKEN` off `process.env` at call time.
 *
 * F436 CHANGED THE MODEL, AND THIS SCREEN WITH IT. Each organization now
 * connects THEIR OWN messaging provider account through the Integrations
 * surface, where the credential is tenant-scoped, encrypted, and never returned.
 * That is the founder constraint made real: a customer is not required to become
 * our provider's customer.
 *
 * The organization admin reading this screen was never the person who could fill
 * in a deployment credential — the secret had to live in the PLATFORM's GCP
 * project, which they have no access to create. Now the credential is theirs and
 * the place to enter it is Integrations, so this page stops asking and starts
 * REPORTING: `providerConnected` and, when it is false,
 * `providerUnavailableReason`, which is one of four things they can act on.
 *
 * "Test connection" is gone from here. It made a real outbound request to test
 * the DEPLOYMENT'S credentials — a question this admin never asked — and it now
 * lives on the integration itself, where it exercises their own credential.
 *
 * Everything below the query is exported as a plain function so the unit tier —
 * `environment: 'node'`, no DOM, no renderer — can test the decisions rather
 * than the markup.
 */

/** The one place the provider-vs-policy distinction is worded. */
export const PROVIDER_NOTE =
  'Your organization connects its own messaging provider account under ' +
  'Integrations, and the credentials stay there \u2014 they are never shown on this ' +
  'page and never returned by the API. What you set here is your policy: whether ' +
  'text messaging is on, and the sender number staff see the message come from.';

/** What the operator has typed, plus what the server says about THIS org. */
export interface NotificationsForm {
  enabled: boolean;
  fromPhone: string;
  /**
   * `providerConnected` from the config read \u2014 whether THIS ORGANIZATION could
   * send right now. Presence only; the API never returns a value, a length or a
   * prefix.
   */
  providerConnected: boolean;
  /**
   * WHICH absence applies when `providerConnected` is false. Carried rather than
   * collapsed into the boolean, because the four values have four different next
   * actions and the operator has to be told which one is theirs.
   */
  providerUnavailableReason: sms.SmsConfig['providerUnavailableReason'];
}

/**
 * The reason Save is blocked, or null.
 *
 * The API's enable rule is now ONE field: a sender number. `From` is a mandatory
 * Twilio parameter and `sms_config_enabled_requires_phone` is the CHECK
 * constraint that survived 1757470000000; the two credential-reference
 * requirements went with their columns.
 *
 * Reproduced here so the operator reads it under the button rather than pressing
 * Save and getting a 422 — a convenience, not a control. The control is
 * `updateSmsConfigSchema` plus that constraint, because the caller of
 * `PUT /api/admin/sms/config` is any holder of `sms:manage`, including curl.
 */
export function saveProblem(form: NotificationsForm): string | null {
  if (!form.enabled) return null;
  if (form.fromPhone.trim() === '') {
    return 'Text messaging cannot be turned on without a sender number.';
  }
  return null;
}

/**
 * The four things that can be missing, each said as the action that fixes it.
 *
 * ONE SENTENCE PER REASON, because there are four different next actions and the
 * previous single warning ("ask whoever runs this deployment") was the right
 * advice for none of them once the credential became the organization's own.
 */
export const UNAVAILABLE_TEXT: Record<
  NonNullable<sms.SmsConfig['providerUnavailableReason']>,
  string
> = {
  no_installation:
    'No messaging provider is connected for your organization, so no text ' +
    'message will be sent even with this turned on. Connect one under Integrations.',
  not_credentialed:
    'Your messaging provider is connected but has no credentials saved, so no ' +
    'text message will be sent. Add them under Integrations.',
  not_verified:
    'Your messaging credentials have not been verified yet, so no text message ' +
    'will be sent. Run the connection test on the integration.',
  paused:
    'Your messaging integration is paused, so no text message will be sent. ' +
    'Turn it back on under Integrations.',
  no_send_capability:
    'The connected messaging product cannot send from this page. Check which ' +
    'product surface the integration is configured for.',
};

/**
 * The thing the operator should know before turning this on, or null.
 *
 * IT WARNS RATHER THAN BLOCKS, deliberately. Setting a sender number before
 * connecting a provider is a legitimate order to do things in, and the API
 * accepts it; a screen TIGHTER than the control it mirrors refuses work the
 * system would have done.
 *
 * What it must not do is stay silent. Enabled with no usable provider, every
 * send fails clean \u2014 nothing is delivered, and nothing on this page says so
 * unless this sentence does.
 */
export function providerWarning(form: NotificationsForm): string | null {
  if (!form.enabled || form.providerConnected) return null;
  return UNAVAILABLE_TEXT[form.providerUnavailableReason ?? 'no_installation'];
}

/**
 * The patch to send — two fields, which is the whole of the configuration.
 *
 * A blank sender number IS sent as null: that field shows its current value, so
 * clearing it is a thing the operator can see themselves doing, and `null` is
 * how this API is told to clear rather than to leave alone.
 */
export function configPatch(form: NotificationsForm): sms.UpdateSmsConfigInput {
  return {
    enabled: form.enabled,
    fromPhoneNumber: form.fromPhone.trim() === '' ? null : form.fromPhone.trim(),
  };
}

/**
 * How an outcome should read.
 *
 * `unknown` is a third tone and not a rounding of the other two. A timeout or
 * an unreadable response means we genuinely cannot say whether anything
 * happened, and both of the confident renderings are wrong: "failed" invites a
 * retry that may duplicate a message, "sent" claims a delivery nobody observed.
 */
export type OutcomeTone = 'ok' | 'unknown' | 'problem';
export interface Outcome {
  tone: OutcomeTone;
  text: string;
}

/**
 * How the per-tenant freshness reading should be worded \u2014 F437 on the screen.
 *
 * `incomplete` and `unobserved` get their own sentences, and that is the point.
 * A forward-only provider has no history to fetch, so "stale" would report every
 * healthy one as broken; and a permanent hole is not a delay that catching up
 * would fix. Rendering either as "out of date" is precisely the
 * confidently-wrong reading the two axes exist to prevent.
 */
export const FRESHNESS_TEXT: Record<sms.SmsFreshness, string> = {
  current: 'Your messaging record is up to date.',
  delayed: 'Your messaging record is a little behind; it should catch up on its own.',
  stale: 'Your messaging record has not been refreshed recently \u2014 check the integration.',
  incomplete:
    'There is a gap in your messaging record that cannot be filled in: this ' +
    'provider does not let us fetch the messages we missed.',
  unobserved: 'We are not yet watching this provider for incoming messages.',
  unknown: 'We do not have a reading on your messaging record yet.',
};

/**
 * WHETHER THIS TRANSPORT MAY CARRY PHI — the axis this screen did not have.
 *
 * `GET /api/admin/sms/status` answers `phiCapable` and `phiRequirementRef`
 * (`messaging-service.ts:1132`), and nothing in the admin app read either, so
 * an administrator learned the state by texting a client and reading a refusal.
 * It is a SEPARATE AXIS from `providerConnected`: an installation can be
 * connected, verified, current and sending staff notifications all day while
 * remaining unapproved for client communication, and the two must never be
 * folded into one "is messaging working" sentence.
 *
 * FOUR STATES, and the two blanks are not the same blank:
 *
 *   authorized   — an active grant exists, made against `requirementRef`.
 *   not_authorized — the server looked and there is no grant. The answer for
 *                  every installation until a Phibian-controlled compliance
 *                  path grants otherwise; no org admin can grant it themselves.
 *   unknown      — the status read has not answered or failed. NOT
 *                  `not_authorized`: we did not look, and printing a refusal we
 *                  did not observe is the same defect in the other direction.
 *   checking     — the read is in flight.
 */
export type PhiTransportState = 'checking' | 'unknown' | 'not_authorized' | 'authorized';

export interface PhiTransportNotice {
  state: PhiTransportState;
  tone: OutcomeTone;
  heading: string;
  text: string;
}

/**
 * Decided from the query, not from a bare boolean.
 *
 * `status` is `undefined` both while loading and after a failure, and
 * `phiCapable` defaulted to `false` would print "not approved for client
 * communication" as a confident finding about an organization nobody asked
 * about. The two are told apart by the query's own flags, which is the only
 * place the difference exists.
 */
export function phiTransportNotice(query: {
  data?: sms.SmsStatus | undefined;
  isLoading?: boolean;
  isError?: boolean;
}): PhiTransportNotice {
  if (query.data === undefined) {
    if (query.isError) {
      return {
        state: 'unknown',
        tone: 'unknown',
        heading: 'We could not check this',
        text:
          'We could not read whether this messaging connection is approved to carry protected ' +
          'health information, so we are not going to say either way. Until it answers, treat ' +
          'it as not approved and keep text messages to staff notifications.',
      };
    }
    return {
      state: 'checking',
      tone: 'unknown',
      heading: 'Checking',
      text: 'Checking whether this messaging connection may carry protected health information.',
    };
  }
  if (!query.data.phiCapable) {
    return {
      state: 'not_authorized',
      tone: 'problem',
      heading: 'Not approved for protected health information',
      text:
        'This messaging connection has not been approved to carry protected health information. ' +
        'Use it for staff notifications only \u2014 do not text people receiving services about ' +
        'their care through it. No one at your organization can grant this from here; it is ' +
        'approved through a Phibian compliance review. This is a statement about this ' +
        'transport, not about whether your organization is HIPAA compliant.',
    };
  }
  const ref = (query.data.phiRequirementRef ?? '').trim();
  return {
    state: 'authorized',
    tone: 'ok',
    heading: 'Approved for protected health information',
    text:
      'This messaging connection has an active approval to carry protected health information' +
      (ref === ''
        ? ', though the criteria it was approved against were not recorded \u2014 ask us to ' +
          're-review it before relying on that.'
        : `, made against ${ref}.`) +
      ' The approval covers this transport only. It is not a claim that your organization is ' +
      'HIPAA compliant, and it does not replace consent from the person you are messaging.',
  };
}

/**
 * A test message that arrived, reported as having arrived.
 *
 * The screen used to read `r.ok` off a body whose keys are
 * `{ success, messageSid, toSuffix }`, get `undefined`, and print "Send
 * failed." for a text message that had genuinely gone out. Resolution is the
 * success signal — every failure is a non-2xx and rejects — so nothing here
 * branches on a field, and the four digits are shown so the operator can
 * confirm WHERE it went without the API ever returning the whole number.
 *
 * `toSuffix` is still checked for shape rather than interpolated blind: this
 * function's entire reason for existing is that the response body was once not
 * what the type claimed.
 */
export function sendTestOutcome(result: sms.SendTestResult): Outcome {
  const suffix = typeof result.toSuffix === 'string' ? result.toSuffix.trim() : '';
  return {
    tone: 'ok',
    text: /^\d{4}$/.test(suffix)
      ? `Test message sent to the number ending ${suffix}.`
      : 'Test message sent.',
  };
}

/**
 * The three send codes that mean WE DO NOT KNOW WHETHER IT WENT.
 *
 * `MessagingSendCode` is an eight-member closed vocabulary
 * (`packages/integration-core/src/messaging.ts`), and it contains two
 * different kinds of answer. `rejected_by_provider`, `recipient_opted_out`,
 * `invalid_credentials`, `rate_limited` and `not_configured` are OBSERVED
 * refusals: the provider
 * answered, and it answered no. These three are not refusals at all —
 *
 *   `timeout`              we stopped waiting; the send may already have gone.
 *   `endpoint_unreachable` the connection failed; where it failed is unknown.
 *   `unexpected_response`  the provider answered something we cannot read.
 *
 * — and painting them the same red as a refusal tells an operator a message
 * definitely did not arrive when nobody measured that. The palette already has
 * the word for it, and `TONE_COLOR.unknown` already exists; before this,
 * nothing on this screen could reach it, because `failureOutcome` read only
 * `error.message` and never the `code` the three failure tables exist to
 * carry.
 */
const UNOBSERVED_SEND_CODES: ReadonlySet<string> = new Set([
  'timeout',
  'endpoint_unreachable',
  'unexpected_response',
]);

/** The `code` an SMS route put on the body, or null. */
export function apiErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (typeof error.body !== 'object' || error.body === null) return null;
  const code = (error.body as { code?: unknown }).code;
  return typeof code === 'string' && code !== '' ? code : null;
}

/**
 * A rejection, said in the API's words where it had any, and toned by whether
 * anybody actually observed it.
 *
 * The API's send failures carry a sentence written for an operator ("the
 * messaging provider refused this message… check the recipient number"), and
 * replacing it with a generic "Send failed." throws away the only part of the
 * answer that tells them what to do next. The TONE is a second question the
 * sentence does not answer: see {@link UNOBSERVED_SEND_CODES}.
 */
export function failureOutcome(error: unknown, fallback: string): Outcome {
  const code = apiErrorCode(error);
  const tone: OutcomeTone =
    code !== null && UNOBSERVED_SEND_CODES.has(code) ? 'unknown' : 'problem';
  const unobserved =
    tone === 'unknown'
      ? ' We could not confirm either way — check with the recipient before sending it again.'
      : '';
  if (error instanceof ApiError && error.message !== '') {
    return { tone, text: `${error.message}${unobserved}` };
  }
  return { tone, text: `${fallback}${unobserved}` };
}

// ---------------------------------------------------------------------------
// Consent — gate 4 of five, and until now the one with no writer
// ---------------------------------------------------------------------------

/**
 * THE E.164 SHAPE THE SERVER ACCEPTS, mirrored so a rejected number is
 * attributable here rather than as an anonymous 422.
 *
 * `recordSmsConsentSchema` in `apps/api/src/domains/sms/schemas.ts` is
 * `/^\+[1-9]\d{6,14}$/`. Mirrored, not imported: the admin bundle does not
 * pull server code, and the two are supposed to drift LOUDLY — a number this
 * accepts and the server rejects still lands as the server's own 422 sentence,
 * which is the failure mode a mirror is allowed to have.
 */
const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * Why this number cannot be sent as a consent record, or null.
 *
 * Checked before the button is enabled, because a consent record is a legal
 * assertion about a specific person and a typo makes it an assertion about
 * somebody else. There is no undo that names the right number for them.
 */
export function consentProblem(toPhoneNumber: string): string | null {
  const trimmed = toPhoneNumber.trim();
  if (trimmed === '') return 'Enter the number this decision is about.';
  if (!E164.test(trimmed)) {
    return 'Use the full number with a country code and no spaces, e.g. +15551234567.';
  }
  return null;
}

/**
 * What was recorded, said back with the LAST FOUR DIGITS ONLY.
 *
 * `POST /api/admin/sms/consent` answers `{ state, addressSuffix }` and
 * deliberately never echoes the number — the audit trail holds four digits and
 * so does the response. This function renders what came back rather than what
 * was typed, so a screen cannot claim a record for a number the server did not
 * confirm.
 */
export function consentOutcome(result: {
  state: 'granted' | 'revoked';
  addressSuffix: string;
}): Outcome {
  const suffix = typeof result.addressSuffix === 'string' ? result.addressSuffix.trim() : '';
  const who = /^\d{4}$/.test(suffix) ? `the number ending ${suffix}` : 'that number';
  return {
    tone: 'ok',
    text:
      result.state === 'granted'
        ? `Consent recorded for ${who}. Messages about their care may now be sent to it.`
        : `Revocation recorded for ${who}. Nothing further will be sent to it, including ` +
          'messages that were already scheduled.',
  };
}

/**
 * WHY THIS CARD CANNOT SHOW YOU WHAT IS ON RECORD.
 *
 * There is no `GET /api/admin/sms/consent`. The consent table is writable and
 * not readable over HTTP, so this screen can record a decision and cannot list
 * decisions — and a card that quietly showed nothing where a list goes would
 * be read as "nobody has consented", which is a different and false statement.
 *
 * The three states matter here more than anywhere else in the product, because
 * the send path treats them as three:
 *
 *   granted  — a client-directed message may be sent.
 *   revoked  — refused, `consent_revoked`.
 *   unknown  — NO ROW. Refused for a client-directed purpose
 *              (`consent_unknown`), allowed for a staff notification. It is
 *              not a soft "revoked" and it is not a soft "granted"; nobody
 *              recorded anything.
 *
 * Absence is unknown by construction: the table's CHECK admits only `granted`
 * and `revoked`, and `resolveConsent` returns `'unknown'` when no row is
 * found. So this copy is describing the data model, not apologising for a
 * missing feature.
 */
export const CONSENT_NO_REGISTER_NOTE =
  'There is no way to list what is already on record — the API can take a decision and cannot ' +
  'hand the list back — so this card cannot tell you whether a number has already been asked. ' +
  'Recording the same decision twice is safe; it replaces the previous one.';

export const CONSENT_UNKNOWN_NOTE =
  'A number nobody has recorded a decision for is UNKNOWN, which is not the same as refused. ' +
  'Messages about a person\u2019s care are refused for an unknown number, and staff ' +
  'notifications still go out \u2014 so a missing record blocks the one and not the other.';

/**
 * Three tones, three tokens. `unsure` is the palette's own word for "we do not
 * know", and using it here is the only reason the connection test's `code` is
 * worth reading: without it a timeout would be painted the same red as a
 * refusal and the reader would act on a failure nobody observed.
 */
export const TONE_COLOR: Record<OutcomeTone, string> = {
  ok: 'var(--color-good-deep)',
  unknown: 'var(--color-unsure-deep)',
  problem: 'var(--color-urgent-deep)',
};

export function NotificationsSettings() {
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ['sms', 'config'], queryFn: () => sms.config() });
  // A SECOND read, deliberately, and not folded into the config one. It is the
  // only place `phiCapable` is served, it is gated on the same `sms:manage`
  // this screen already requires, and it makes no outbound call.
  const status = useQuery({ queryKey: ['sms', 'status'], queryFn: () => sms.status() });
  const phi = phiTransportNotice(status);

  const [enabled, setEnabled] = useState(false);
  const [fromPhone, setFromPhone] = useState('');
  const [testTo, setTestTo] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [consentTo, setConsentTo] = useState('');
  const [consentResult, setConsentResult] = useState<Outcome | null>(null);

  // `config.data` is `null` for an organization that has never saved — a real
  // answer, and the one where the defaults above are the honest starting point.
  useEffect(() => {
    if (config.data) {
      setEnabled(config.data.enabled);
      setFromPhone(config.data.fromPhoneNumber ?? '');
    }
  }, [config.data]);

  // Defaults to `false` while the read is in flight or has failed, which is the
  // safe direction: it produces a warning that may turn out to be unnecessary,
  // where `true` would produce a silence that may turn out to be wrong.
  const form: NotificationsForm = {
    enabled,
    fromPhone,
    // Defaults to `false` while the read is in flight or has failed, which is
    // the safe direction: it produces a warning that may turn out to be
    // unnecessary, where `true` would produce a silence that may turn out to be
    // wrong.
    providerConnected: config.data?.providerConnected ?? false,
    providerUnavailableReason: config.data?.providerUnavailableReason ?? null,
  };
  const blocked = saveProblem(form);
  const warning = providerWarning(form);

  const save = useMutation({
    mutationFn: () => sms.updateConfig(configPatch(form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms', 'config'] });
    },
  });

  const sendTest = useMutation({
    mutationFn: () => sms.sendTest(testTo),
    onSuccess: (r) => setOutcome(sendTestOutcome(r)),
    onError: (err) => setOutcome(failureOutcome(err, 'The test message wasn’t sent.')),
  });

  /**
   * ONE mutation, TWO buttons, and the pending label keyed to the DIRECTION
   * that is actually running.
   *
   * `recordConsent.isPending` is true for both buttons at once, so labelling
   * both from it puts "Recording…" on Grant while the operator pressed Revoke
   * — the exact defect this product has already shipped once on an
   * Approve/Deny pair. `recordConsent.variables` is the argument the running
   * mutation was called with, so the label follows the press.
   */
  const recordConsent = useMutation({
    mutationFn: (state: 'granted' | 'revoked') => sms.recordConsent(consentTo.trim(), state),
    onSuccess: (r) => setConsentResult(consentOutcome(r)),
    onError: (err) =>
      setConsentResult(
        failureOutcome(err, 'That decision wasn’t recorded, so nothing about this number changed.'),
      ),
  });
  const consentBlocked = consentProblem(consentTo);
  const consentRunning = recordConsent.isPending ? recordConsent.variables : undefined;

  // A detail card, not a list: `listPhase` with no row count never returns
  // `empty`, so the `empty` slot below is unreachable and says so.
  const phase = listPhase(config);

  return (
    <div className="rn-col" style={{ maxWidth: 620 }}>
      <SettingsHeader title="Notifications & SMS" />

      <ListState
        phase={phase}
        surface="table"
        rows={4}
        failedMessage="We couldn’t load SMS settings just now, so this form isn’t showing your current settings."
        onRetry={() => void config.refetch()}
        empty={null}
      >
        <SettingsCard>
          <h2 className="rn-card-title">Text messaging</h2>
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
            <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span>Send text notifications</span>
            </label>

            <SettingsField
              label="Sender number"
              hint="The number staff will see the message come from. Digits with a country code, e.g. +15551234567."
            >
              <input
                style={{ marginBottom: 0 }}
                placeholder="+15551234567"
                value={fromPhone}
                onChange={(e) => setFromPhone(e.target.value)}
              />
            </SettingsField>

            <ConsequenceNote text="Turning this on sends real text messages to staff. Test it first." />

            {warning ? (
              <p
                className="rn-muted"
                role="status"
                aria-live="polite"
                style={{ margin: 0, color: 'var(--color-unsure-deep)' }}
              >
                {warning}
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: 'var(--space-12)', alignItems: 'center' }}>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={save.isPending || blocked !== null}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              <span
                className="rn-muted"
                role="status"
                aria-live="polite"
                style={blocked ? { color: 'var(--color-urgent-deep)' } : undefined}
              >
                {blocked ?? (save.isSuccess ? 'Saved' : '')}
              </span>
            </div>
            {save.isError ? (
              <p style={{ margin: 0, color: 'var(--color-urgent-deep)' }} role="status">
                {failureOutcome(save.error, 'Those settings weren’t saved.').text}
              </p>
            ) : null}
          </div>
        </SettingsCard>

        <SettingsCard>
          <h2 className="rn-card-title">Where messages are sent from</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            {PROVIDER_NOTE}
          </p>
          <p style={{ marginTop: 'var(--space-12)', marginBottom: 0 }} role="status">
            {config.data?.providerConnected
              ? 'Your organization has a messaging provider connected and verified.'
              : UNAVAILABLE_TEXT[config.data?.providerUnavailableReason ?? 'no_installation']}
          </p>
          {config.data?.providerConnected ? (
            <p className="rn-muted" style={{ marginTop: 'var(--space-8)', marginBottom: 0 }}>
              {FRESHNESS_TEXT[config.data.providerFreshness]}
            </p>
          ) : null}
        </SettingsCard>
      </ListState>

      {/* OUTSIDE the config `ListState`, on purpose: this reads a different
          endpoint, and a failed config read must not take the PHI answer off
          the screen with it. Above "Test it" because it governs who may be
          texted at all. */}
      <SettingsCard>
        <h2 className="rn-card-title">Protected health information</h2>
        <p
          role="status"
          aria-live="polite"
          style={{ marginTop: 'var(--space-8)', marginBottom: 0, color: TONE_COLOR[phi.tone] }}
        >
          <strong>{phi.heading}.</strong>
        </p>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)', marginBottom: 0 }}>
          {phi.text}
        </p>
      </SettingsCard>

      {/* OUTSIDE the config `ListState` for the same reason the PHI card is:
          consent is not read from `GET /sms/config`, and a failed config read
          must not take the only writer of gate 4 off the screen. */}
      <SettingsCard>
        <h2 className="rn-card-title">Consent to be texted</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)', marginBottom: 0 }}>
          {CONSENT_UNKNOWN_NOTE}
        </p>
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)', marginBottom: 0 }}>
          {CONSENT_NO_REGISTER_NOTE}
        </p>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
          <SettingsField
            label="Their number"
            hint="The number the decision is about. Digits with a country code, e.g. +15551234567."
          >
            <input
              style={{ marginBottom: 0 }}
              placeholder="+15551234567"
              value={consentTo}
              onChange={(e) => {
                setConsentTo(e.target.value);
                // The previous answer described the PREVIOUS number. Leaving it
                // up while a new one is typed is how somebody reads a record as
                // confirmed for a number nobody sent.
                setConsentResult(null);
              }}
            />
          </SettingsField>

          <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="rn-link"
              disabled={recordConsent.isPending || consentBlocked !== null}
              onClick={() => recordConsent.mutate('granted')}
            >
              {consentRunning === 'granted' ? 'Recording…' : 'They agreed to be texted'}
            </button>
            <button
              type="button"
              className="rn-link"
              disabled={recordConsent.isPending || consentBlocked !== null}
              onClick={() => recordConsent.mutate('revoked')}
            >
              {consentRunning === 'revoked' ? 'Recording…' : 'They asked us to stop'}
            </button>
          </div>

          {consentBlocked !== null && consentTo.trim() !== '' ? (
            <p
              className="rn-muted"
              role="status"
              aria-live="polite"
              style={{ margin: 0, color: 'var(--color-urgent-deep)' }}
            >
              {consentBlocked}
            </p>
          ) : null}

          {consentResult ? (
            <p
              className="rn-muted"
              role="status"
              aria-live="polite"
              style={{ margin: 0, color: TONE_COLOR[consentResult.tone] }}
            >
              {consentResult.text}
            </p>
          ) : null}
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Test it</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
          “Send test” sends a real text message from your sender number, through your organization’s
          own provider account. To check the credentials themselves without sending anything, use
          “Test connection” on the integration under Integrations.
        </p>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
            <input
              style={{ marginBottom: 0 }}
              placeholder="+15557654321"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button
              type="button"
              className="rn-link"
              disabled={sendTest.isPending || testTo.trim() === ''}
              onClick={() => sendTest.mutate()}
            >
              {sendTest.isPending ? 'Sending…' : 'Send test'}
            </button>
          </div>
          {outcome ? (
            <p
              className="rn-muted"
              role="status"
              aria-live="polite"
              style={{ margin: 0, color: TONE_COLOR[outcome.tone] }}
            >
              {outcome.text}
            </p>
          ) : null}
        </div>
      </SettingsCard>
    </div>
  );
}
